import React, { useMemo, useCallback } from 'react';
import { MdLocalShipping } from 'react-icons/md';
import { State } from 'country-state-city';
import { FormData, FormErrors } from '../../hooks/useCheckoutForm';
import SearchableSelect from '../common/SearchableSelect';

interface DeliveryFormProps {
  formData: FormData;
  errors: FormErrors;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onDeliveryQuote: () => void;
  orderLoading: boolean;
  submittingMethod: 'takeout' | 'pay_online' | 'delivery' | null;
  cartItemsCount: number;
}

export const DeliveryForm: React.FC<DeliveryFormProps> = ({
  formData,
  errors,
  handleChange,
  onDeliveryQuote,
  orderLoading,
  submittingMethod,
  cartItemsCount,
}) => {
  // Get US states for searchable dropdown
  const usStates = useMemo(() => {
    return State.getStatesOfCountry('US').map(state => ({
      value: state.isoCode,
      label: state.name
    }));
  }, []);

  // Handle searchable select change (converts to match existing handleChange)
  const handleSearchSelectChange = useCallback((name: string, value: string) => {
    handleChange({ target: { name, value } } as any);
  }, [handleChange]);

  // Real-time validation check for Continue button
  const isFormValid = useMemo(() => {
    const hasName = !!formData.name?.trim();
    const hasPhone = !!formData.phone?.trim();
    const isPhoneValid = /^\d{10,15}$/.test(formData.phone?.replace(/[^\d]/g, '') || '');
    const hasAddress = !!formData.address_line_1?.trim();
    const hasCity = !!formData.locality?.trim();
    const hasState = !!formData.administrative_district_level_1?.trim();
    const hasZip = !!formData.postal_code?.trim();
    const isEmailValid = !formData.email?.trim() || /\S+@\S+\.\S+/.test(formData.email);
    
    return hasName && hasPhone && isPhoneValid && hasAddress && hasCity && hasState && hasZip && isEmailValid;
  }, [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onDeliveryQuote();
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      {/* Personal Information - 2 Column Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your full name"
            aria-describedby={errors.name ? 'name-error' : undefined}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p id="name-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your phone number"
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address (Optional)
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your email address (optional)"
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      {/* Delivery Address Fields */}
      <div>
        <label htmlFor="address_line_1" className="block text-sm font-medium text-gray-700 mb-1">
          Street Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="address_line_1"
          name="address_line_1"
          value={formData.address_line_1 || ''}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            errors.address_line_1 ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter street address"
          aria-describedby={errors.address_line_1 ? 'address-line-1-error' : undefined}
          aria-invalid={!!errors.address_line_1}
        />
        {errors.address_line_1 && (
          <p id="address-line-1-error" className="mt-1 text-sm text-red-500" role="alert">
            {errors.address_line_1}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="locality" className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="locality"
            name="locality"
            value={formData.locality || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.locality ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="City"
            aria-describedby={errors.locality ? 'locality-error' : undefined}
            aria-invalid={!!errors.locality}
          />
          {errors.locality && (
            <p id="locality-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.locality}
            </p>
          )}
        </div>

        <div>
          <SearchableSelect
            label="State"
            name="administrative_district_level_1"
            value={formData.administrative_district_level_1 || ''}
            onChange={handleSearchSelectChange}
            options={usStates}
            placeholder="Search state..."
            required
          />
          {errors.administrative_district_level_1 && (
            <p id="state-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.administrative_district_level_1}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="postal_code"
            name="postal_code"
            value={formData.postal_code || ''}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.postal_code ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="ZIP Code"
            aria-describedby={errors.postal_code ? 'postal-code-error' : undefined}
            aria-invalid={!!errors.postal_code}
          />
          {errors.postal_code && (
            <p id="postal-code-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.postal_code}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-1">
          Special Requests (Optional)
        </label>
        <textarea
          id="specialRequests"
          name="specialRequests"
          value={formData.specialRequests}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900"
          placeholder="Any special instructions for your order"
        />
      </div>

      {/* Delivery Continue Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={orderLoading || cartItemsCount === 0 || !isFormValid}
          className={`w-full bg-red-500 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
            (orderLoading || cartItemsCount === 0 || !isFormValid) 
              ? 'opacity-70 cursor-not-allowed' 
              : 'hover:bg-red-600'
          }`}
        >
          {orderLoading && submittingMethod === 'delivery' ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" aria-hidden="true"></div>
          ) : (
            <MdLocalShipping className="w-5 h-5 mr-2" aria-hidden="true" />
          )}
          Continue
        </button>
      </div>
    </form>
  );
};
